import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { OwnerApprovalLine, SendToOwnersDialog } from "@/components/OwnerApproval";
import { Plus, Loader2, Inbox, Paperclip, Check, X, Trash2, FileText, MessageSquare, Send } from "lucide-react";

const CATEGORIES = ["Repair", "Purchase", "Maintenance", "Other"];
const STATUS_STYLE = {
  pending: "bg-amber-500/15 text-amber-600",
  approved: "bg-emerald-500/15 text-emerald-600",
  declined: "bg-destructive/15 text-destructive",
  ordered: "bg-blue-500/15 text-blue-600",
  completed: "bg-primary/15 text-primary",
};
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const EMPTY = { property_id: "", title: "", category: "Repair", description: "", estimated_cost: "" };

export default function Requests() {
  const { user } = useAuth();
  const isAdmin = can(user, ["admin", "regional"]);
  const isOwner = user?.role === "owner";
  const [ownerSend, setOwnerSend] = useState(null);
  const focusId = new URLSearchParams(window.location.search).get("id");
  const [props, setProps] = useState([]);
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [decideFor, setDecideFor] = useState(null);
  const [decideStatus, setDecideStatus] = useState("approved");
  const [decideNote, setDecideNote] = useState("");
  const [filterPid, setFilterPid] = useState("all");

  // thread / discussion
  const [threadId, setThreadId] = useState(null);
  const [threadText, setThreadText] = useState("");
  const [threadFile, setThreadFile] = useState(null);
  const [threadSaving, setThreadSaving] = useState(false);
  const [fileKey, setFileKey] = useState(0);
  const openThread = list?.find((r) => r.id === threadId) || null;

  const postComment = async () => {
    if (!threadText.trim() && !threadFile) return toast.error("Add a comment or attach a file");
    setThreadSaving(true);
    try {
      const fd = new FormData();
      const params = new URLSearchParams({ text: threadText });
      if (threadFile) fd.append("file", threadFile);
      await api.post(`/requests/${threadId}/comments?${params.toString()}`, fd);
      setThreadText("");
      setThreadFile(null);
      setFileKey((k) => k + 1);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setThreadSaving(false);
    }
  };

  useEffect(() => {
    api.get("/properties").then((r) => setProps(r.data));
    api.post("/comms/seen/requests").catch(() => {});
  }, []);

  const load = useCallback(() => {
    setList(null);
    const params = filterPid && filterPid !== "all" ? { property_id: filterPid } : {};
    api.get("/requests", { params }).then((r) => setList(r.data)).catch(() => setList([]));
  }, [filterPid]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (focusId && list?.length) document.querySelector(`[data-testid="request-card-${focusId}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusId, list]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm({ ...EMPTY, property_id: props[0]?.id || "" }); setPhoto(null); setOpen(true); };

  const submit = async () => {
    if (!form.property_id) return toast.error("Select a property");
    if (!form.title.trim()) return toast.error("Enter a title");
    setSaving(true);
    try {
      const fd = new FormData();
      const params = new URLSearchParams({
        property_id: form.property_id, title: form.title, category: form.category,
        description: form.description, estimated_cost: form.estimated_cost,
      });
      if (photo) fd.append("file", photo);
      await api.post(`/requests?${params.toString()}`, fd);
      toast.success("Request submitted");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openDecide = (r, status) => { setDecideFor(r); setDecideStatus(status); setDecideNote(r.admin_note || ""); };
  const saveDecision = async () => {
    try {
      if (isOwner) await api.post(`/requests/${decideFor.id}/owner-decision`, { decision: decideStatus, note: decideNote });
      else await api.patch(`/requests/${decideFor.id}/status`, { status: decideStatus, note: decideNote });
      toast.success(`Request ${decideStatus}`);
      setDecideFor(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete request "${r.title}"?`)) return;
    await api.delete(`/requests/${r.id}`);
    toast.success("Request deleted");
    load();
  };

  return (
    <div className="space-y-6" data-testid="requests-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps text-primary">Communications</div>
          <h1 className="font-heading text-3xl font-black tracking-tight lg:text-4xl">Requests</h1>
          <p className="mt-1 text-sm text-muted-foreground">Submit property item/repair requests for admin approval. Full history kept here.</p>
        </div>
        <div className="flex items-center gap-2">
          {props.length > 1 && (
            <div className="w-48">
              <Select value={filterPid} onValueChange={setFilterPid}>
                <SelectTrigger data-testid="requests-property-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Properties</SelectItem>
                  {props.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          )}
          {!isOwner && <Button onClick={openNew} data-testid="new-request-btn"><Plus className="mr-2 h-4 w-4" /> New Request</Button>}
        </div>
      </div>

      {list === null ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : list.length === 0 ? (
        <Card className="rounded-none border p-12 text-center">
          <Inbox className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">No requests yet.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {list.map((r) => (
            <Card key={r.id} className={`rounded-none border p-4 ${focusId === r.id ? "ring-2 ring-primary" : ""}`} data-testid={`request-card-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-heading text-base font-bold">{r.title}</span>
                    <Badge variant="secondary" className="rounded-none">{r.category}</Badge>
                    <span className={`rounded-none px-2 py-0.5 text-xs font-semibold uppercase ${STATUS_STYLE[r.status] || ""}`} data-testid={`request-status-${r.id}`}>{r.status}</span>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {r.property_name} · by {r.created_by} · Est. {money(r.estimated_cost)}
                  </div>
                  {r.description && <p className="mt-2 whitespace-pre-wrap text-sm">{r.description}</p>}
                  <OwnerApprovalLine r={r} />
                  {r.admin_note && <p className="mt-2 text-sm text-muted-foreground"><span className="font-semibold">{r.decided_by_role === "owner" ? "Owner note:" : "Admin note:"}</span> {r.admin_note}{r.decided_by ? ` — ${r.decided_by}` : ""}</p>}
                  {r.file && (
                    <a href={`${API_BASE}/requests/${r.id}/file`} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline" data-testid={`request-file-${r.id}`}>
                      <Paperclip className="h-3 w-3" /> {r.file.filename || "Attachment"}
                    </a>
                  )}
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  {!isOwner && (
                    <Button size="sm" variant="outline" onClick={() => setThreadId(r.id)} data-testid={`request-discuss-${r.id}`}>
                      <MessageSquare className="mr-1 h-3.5 w-3.5" /> Discuss{r.comments?.length ? ` (${r.comments.length})` : ""}
                    </Button>
                  )}
                  {isAdmin && r.status === "pending" && (
                    <Button size="sm" variant="outline" className="border-violet-400 text-violet-700 dark:text-violet-300" onClick={() => setOwnerSend(r)} data-testid={`request-send-owner-${r.id}`}>
                      <Send className="mr-1 h-3.5 w-3.5" /> {r.owner_approval ? "Resend to Owner" : "Send to Owner for Approval"}
                    </Button>
                  )}
                  {isOwner && r.status === "pending" && (r.owner_approval?.sent_to || []).some((o) => o.id === user?.id) && (
                    <>
                      <Button size="sm" onClick={() => openDecide(r, "approved")} data-testid={`request-owner-approve-${r.id}`}><Check className="mr-1 h-3.5 w-3.5" /> Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => openDecide(r, "declined")} data-testid={`request-owner-decline-${r.id}`}><X className="mr-1 h-3.5 w-3.5" /> Decline</Button>
                    </>
                  )}
                  {isAdmin && r.status === "pending" && (
                    <>
                      <Button size="sm" onClick={() => openDecide(r, "approved")} data-testid={`request-approve-${r.id}`}><Check className="mr-1 h-3.5 w-3.5" /> Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => openDecide(r, "declined")} data-testid={`request-decline-${r.id}`}><X className="mr-1 h-3.5 w-3.5" /> Decline</Button>
                    </>
                  )}
                  {isAdmin && r.status === "approved" && (
                    <Button size="sm" variant="outline" onClick={() => openDecide(r, "ordered")} data-testid={`request-order-${r.id}`}>Mark Ordered</Button>
                  )}
                  {isAdmin && r.status === "ordered" && (
                    <Button size="sm" variant="outline" onClick={() => openDecide(r, "completed")} data-testid={`request-complete-${r.id}`}>Mark Completed</Button>
                  )}
                  {(user?.role === "admin" || r.created_by_id === user?.id) && (
                    <Button size="icon" variant="ghost" onClick={() => remove(r)} data-testid={`request-delete-${r.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <SendToOwnersDialog request={ownerSend} onClose={() => setOwnerSend(null)} onSent={() => { setOwnerSend(null); load(); }} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="request-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">New Request</DialogTitle>
            <DialogDescription>Describe what's needed (e.g., pool heater failed — need a new heater).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Property</Label>
                <Select value={form.property_id} onValueChange={(v) => setF("property_id", v)}>
                  <SelectTrigger data-testid="request-property"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{props.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setF("category", v)}>
                  <SelectTrigger data-testid="request-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setF("title", e.target.value)} placeholder="e.g., Replace pool heater" data-testid="request-title" />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setF("description", e.target.value)} rows={3} data-testid="request-description" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Estimated Cost ($)</Label>
                <Input type="text" inputMode="decimal" value={form.estimated_cost} onChange={(e) => setF("estimated_cost", e.target.value)} data-testid="request-cost" />
              </div>
              <div className="space-y-1.5">
                <Label>Photo / Attachment</Label>
                <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setPhoto(e.target.files?.[0] || null)} data-testid="request-photo" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="request-submit-btn">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!decideFor} onOpenChange={(o) => !o && setDecideFor(null)}>
        <DialogContent data-testid="decide-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading capitalize">{decideStatus} Request</DialogTitle>
            <DialogDescription>{decideFor?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label>Note {decideStatus === "declined" ? "(reason)" : "(optional)"}</Label>
            <Textarea value={decideNote} onChange={(e) => setDecideNote(e.target.value)} rows={3} data-testid="decide-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecideFor(null)}>Cancel</Button>
            <Button onClick={saveDecision} data-testid="decide-save-btn">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!threadId} onOpenChange={(o) => !o && setThreadId(null)}>
        <DialogContent className="max-w-2xl" data-testid="thread-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{openThread?.title}</DialogTitle>
            <DialogDescription>
              {openThread?.property_name} · <span className="capitalize">{openThread?.status}</span> — everyone on this request can post updates and attachments here.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[45vh] space-y-3 overflow-auto pr-1">
            {/* original request as first bubble */}
            {openThread && (
              <div className="border-l-2 border-primary/40 bg-secondary/40 px-3 py-2" data-testid="thread-original">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold">{openThread.created_by}</span>
                  <span className="text-muted-foreground">Original request</span>
                </div>
                {openThread.description && <p className="mt-1 whitespace-pre-wrap text-sm">{openThread.description}</p>}
                {openThread.file && (
                  <a href={`${API_BASE}/requests/${openThread.id}/file`} target="_blank" rel="noreferrer" className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline">
                    <Paperclip className="h-3 w-3" /> {openThread.file.filename || "Attachment"}
                  </a>
                )}
              </div>
            )}
            {openThread?.admin_note && (
              <div className="border-l-2 border-amber-500/50 bg-amber-500/5 px-3 py-2">
                <div className="text-xs font-semibold">{openThread.decided_by || "Admin"} <span className="font-normal text-muted-foreground">· admin note</span></div>
                <p className="mt-1 whitespace-pre-wrap text-sm">{openThread.admin_note}</p>
              </div>
            )}
            {(openThread?.comments || []).map((c) => (
              <div key={c.id} className="border-l-2 border-border px-3 py-2" data-testid={`thread-comment-${c.id}`}>
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold">{c.user_name} <span className="font-normal capitalize text-muted-foreground">· {c.role}</span></span>
                  <span className="text-muted-foreground">{new Date(c.created_at).toLocaleString()}</span>
                </div>
                {c.text && <p className="mt-1 whitespace-pre-wrap text-sm">{c.text}</p>}
                {c.file && (
                  <a href={`${API_BASE}/requests/${openThread.id}/comments/${c.id}/file`} target="_blank" rel="noreferrer" className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline" data-testid={`thread-file-${c.id}`}>
                    <Paperclip className="h-3 w-3" /> {c.file.filename || "Attachment"}
                  </a>
                )}
              </div>
            ))}
            {openThread && !(openThread.comments || []).length && (
              <p className="py-2 text-center text-xs text-muted-foreground">No replies yet. Start the conversation below.</p>
            )}
          </div>

          <div className="space-y-2 border-t border-border pt-3">
            <Textarea value={threadText} onChange={(e) => setThreadText(e.target.value)} rows={2} placeholder="Write a reply…" data-testid="thread-input" />
            <div className="flex items-center justify-between gap-2">
              <Input key={fileKey} type="file" accept=".pdf,.jpg,.jpeg,.png" className="max-w-[60%]" onChange={(e) => setThreadFile(e.target.files?.[0] || null)} data-testid="thread-file-input" />
              <Button onClick={postComment} disabled={threadSaving} data-testid="thread-send-btn">
                {threadSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Post
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
